import styled from 'styled-components';
import { BodyRegular, Card, Flex, Headline4 } from 'components/Elements';
import { Course, getCourseGenerateStatus } from 'models/Course';
import { breakpoints, sizes } from 'theme';
import strings from '../../../strings/strings.json';
import Button from 'components/buttons/Button';
import BuildIcon from 'assets/icons/BuildIcon';
import { useNavigate } from 'react-router-dom';
import { PercentageProgressBar } from 'components/progressBar/ProgressBar';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import NextIcon from 'assets/icons/controls/NextIcon';
import { useElementWidth } from 'hooks/useElementWidth';

type CourseCardProps = {
    course: Course;
};

export default function CourseCard({ course }: CourseCardProps): JSX.Element {
    const navigate = useNavigate();
    const [ref, width] = useElementWidth<HTMLDivElement>();

    if (width === undefined) {
        return <CourseCardContainer ref={ref} />;
    }
    const isNarrow = width <= 450;

    let body = <></>;

    const courseGenerateStatus = getCourseGenerateStatus(course);
    switch (courseGenerateStatus) {
        case 'generating':
            body = (
                <PercentageProgressBar
                    label={strings.OACoursesTable.generateProgressBarLabel}
                    dateStart={course.dateGenerateStart!}
                    expectedMinsToComplete={1}
                />
            );
            break;
        case 'error':
            body = <ErrorNotifierSlim description={strings.OACoursesTable.generateCourseError} />;
            break;
        case 'none':
        case 'complete':
        default:
            body = (
                <DateAndButtonsContainer isnarrow={isNarrow}>
                    <BodyRegular>
                        {strings.trainerDashboard.dateCreated} {formatDate(course.dateCreated)}
                    </BodyRegular>
                    <Button
                        title={strings.trainerDashboard.buildCourses.buildCourseButton.title}
                        aria={strings.trainerDashboard.buildCourses.buildCourseButton.aria}
                        icon={<BuildIcon />}
                        onClick={() => {
                            navigate(`/build-course/${course.id}`);
                        }}
                        fullwidth={isNarrow}
                    />
                </DateAndButtonsContainer>
            );
            break;
    }

    return (
        <CourseCardContainer ref={ref}>
            <Headline4>{course.title}</Headline4>
            {body}
        </CourseCardContainer>
    );
}

export function PublishedCourseCard({ course }: CourseCardProps): JSX.Element {
    const navigate = useNavigate();
    const [ref, width] = useElementWidth<HTMLDivElement>();

    if (width === undefined) {
        return <CourseCardContainer ref={ref} />;
    }

    const isNarrow = width <= 500;

    return (
        <CourseCardContainer ref={ref}>
            <Headline4>{course.title}</Headline4>
            <DateAndButtonsContainer isnarrow={isNarrow}>
                <BodyRegular>
                    {strings.trainerDashboard.dateCreated} {formatDate(course.dateCreated)}
                </BodyRegular>
                <Button
                    title={strings.trainerDashboard.scheduleCourses.viewButton.title}
                    aria={`${strings.trainerDashboard.scheduleCourses.viewButton.aria}: ${course.title}`}
                    icon={<NextIcon />}
                    onClick={() => {
                        navigate(`/published-course/${course.id}`);
                    }}
                    iconOnRight
                    fullwidth={isNarrow}
                />
            </DateAndButtonsContainer>
        </CourseCardContainer>
    );
}

const DateAndButtonsContainer = styled(Flex)<{ isnarrow: boolean }>`
    justify-content: space-between;
    width: 100%;
    gap: ${sizes.spacingMd};
    flex-direction: ${({ isnarrow }) => (isnarrow ? 'column' : 'row')};
    align-items: ${({ isnarrow }) => (isnarrow ? 'start' : 'center')};
`;

const CourseCardContainer = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
    justify-content: space-between;

    @media (max-width: ${breakpoints.sm}) {
        gap: ${sizes.spacingMd};
    }
`;

function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}
